export const SHOW_GUIDE_MANAGER_PANEL = true;
export const GUIDE_STATE_PLACEHOLDER_TEXT = "Waiting for guide state...";
export const MICROPHONE_DENIED_GUIDE_CONTINUE_DELAY_MS = 3500;
export const GUIDE_AUDIO_LEVEL_REQUEST_INTERVAL_MS = 33;

const GUIDE_STATE_ENUM_LABELS = {
  0: "GUIDE_UNSTARTED",
  1: "GUIDE_UNSTARTED_ERROR",
  2: "GUIDE_STARTED",
  3: "INSIGHT_UNSTARTED",
  4: "INSIGHT_UNSTARTED_ERROR",
  5: "INSIGHT_STARTED",
  6: "INSIGHT_STARTED_ERROR",
  7: "TALKING",
  8: "TALKING_ERROR",
  9: "LISTENING",
  10: "LISTENING_ERROR",
  11: "THINKING",
  12: "THINKING_ERROR",
  13: "WAITING",
  14: "WAITING_ERROR",
  15: "INSIGHT_COMPLETED",
  16: "INSIGHT_COMPLETED_ERROR",
  17: "INSIGHT_CANCELLED",
  18: "INSIGHT_CANCELLED_ERROR",
};
const GUIDE_STATE_ENUM_VALUES = new Set(Object.values(GUIDE_STATE_ENUM_LABELS));

const GUIDE_AUDIO_LEVEL_FIELDS = [
  "audioLevel",
  "audioVolume",
  "guideAudioLevel",
  "guideAudioVolume",
  "level",
  "volume",
  "amplitude",
  "rms",
  "value",
];
const GUIDE_STEP_COLLECTION_FIELDS = [
  "insightDatas",
  "steps",
  "guideSteps",
  "guide_steps",
  "insights",
  "items",
];
const GUIDE_STEP_ID_FIELDS = [
  "insightId",
  "insightID",
  "insight_id",
  "assessmentId",
  "assessmentID",
  "assessment_id",
  "stepId",
  "stepID",
  "step_id",
  "id",
  "value",
];
const GUIDE_STEP_ORDER_FIELDS = [
  "order",
  "sortOrder",
  "sort_order",
  "position",
  "index",
  "sequence",
];

const hasOwn = (payload, field) =>
  Boolean(payload) && Object.prototype.hasOwnProperty.call(payload, field);

const normalizeGuideText = (value) =>
  typeof value === "string" ? value.trim() : "";

const normalizeGuideId = (value) => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  return "";
};

const getNumberField = (value, fields) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  for (const field of fields) {
    const number = Number(value[field]);
    if (Number.isFinite(number)) {
      return number;
    }
  }

  return null;
};

const getIdField = (value, fields) => {
  const directId = normalizeGuideId(value);
  if (directId) {
    return directId;
  }

  if (!value || typeof value !== "object") {
    return "";
  }

  for (const field of fields) {
    const id = normalizeGuideId(value[field]);
    if (id) {
      return id;
    }
  }

  const nestedCandidates = [
    value.insight,
    value.assessment,
    value.step,
    value.node,
    value.data && typeof value.data === "object" ? value.data : null,
  ];

  for (const nestedValue of nestedCandidates) {
    const nestedId = getIdField(nestedValue, fields);
    if (nestedId) {
      return nestedId;
    }
  }

  return "";
};

const getGuideStepId = (step) => getIdField(step, GUIDE_STEP_ID_FIELDS);

const toGuideStepArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === "object") {
    return Object.values(value);
  }

  return [];
};

const sortGuideSteps = (steps) => {
  const stepsWithOrder = steps.map((step, index) => ({
    step,
    index,
    order: getNumberField(step, GUIDE_STEP_ORDER_FIELDS),
  }));

  if (!stepsWithOrder.some(({ order }) => order !== null)) {
    return steps;
  }

  return [...stepsWithOrder]
    .sort((a, b) => {
      const orderA = a.order ?? a.index;
      const orderB = b.order ?? b.index;
      if (orderA !== orderB) {
        return orderA - orderB;
      }

      return a.index - b.index;
    })
    .map(({ step }) => step);
};

const getGuideStepCollection = (guideData) => {
  const roots = [
    guideData,
    guideData?.data,
    guideData?.guide,
    guideData?.structure,
    guideData?.content,
  ].filter(Boolean);

  for (const root of roots) {
    for (const field of GUIDE_STEP_COLLECTION_FIELDS) {
      const steps = toGuideStepArray(root[field]);
      if (steps.length > 0) {
        return sortGuideSteps(steps);
      }
    }
  }

  return [];
};

export const getOrderedGuideStepIds = (guideData) =>
  getGuideStepCollection(guideData)
    .filter((step) => !Array.isArray(step?.insightTriggers) ||
      !step.insightTriggers.some((trigger) => Number(trigger?.insightTriggerType) === 5))
    .map(getGuideStepId)
    .filter(Boolean);

// Unity resolves the active instruction target and bookmark; React only presents it.
export const normalizeGuideMapTarget = (payload) => {
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return null;
    }
  }
  if (!payload || typeof payload !== "object") return null;

  const guideId = normalizeGuideText(payload.guideId);
  const insightId = normalizeGuideText(payload.insightId);
  const environmentId = normalizeGuideText(payload.environmentId);
  const markerState = Number(payload.markerState);
  if (!guideId || !insightId || !environmentId || ![0, 1, 2].includes(markerState)) {
    return null;
  }
  return { guideId, insightId, environmentId, markerState };
};

export const getLastGuideStepId = (guideData) => {
  const stepIds = getOrderedGuideStepIds(guideData);
  return stepIds[stepIds.length - 1] || "";
};

export const getInsightIdFromPayload = (payload) => {
  if (payload == null) {
    return "";
  }

  if (typeof payload === "string") {
    const trimmedPayload = payload.trim();
    if (!trimmedPayload) {
      return "";
    }

    try {
      return getInsightIdFromPayload(JSON.parse(trimmedPayload));
    } catch {
      return trimmedPayload;
    }
  }

  return getIdField(payload, GUIDE_STEP_ID_FIELDS);
};

export const guideStepIdsMatch = (firstId, secondId) => {
  const normalizedFirstId = normalizeGuideId(firstId);
  const normalizedSecondId = normalizeGuideId(secondId);
  return Boolean(
    normalizedFirstId &&
      normalizedSecondId &&
      normalizedFirstId === normalizedSecondId,
  );
};

const getNumericGuideState = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();
  if (!/^-?\d+$/.test(trimmedValue)) {
    return null;
  }

  const number = Number(trimmedValue);
  return Number.isFinite(number) ? number : null;
};

export const normalizeGuideStateValue = (value) => {
  if (value == null) {
    return "";
  }

  const numericState = getNumericGuideState(value);
  if (numericState !== null && GUIDE_STATE_ENUM_LABELS[numericState]) {
    return GUIDE_STATE_ENUM_LABELS[numericState];
  }

  const upperValue = String(value).trim().toUpperCase();
  if (!upperValue) {
    return "";
  }

  if (GUIDE_STATE_ENUM_VALUES.has(upperValue)) {
    return upperValue;
  }

  if (upperValue.includes("INSIGHT_COMPLETED")) return "INSIGHT_COMPLETED";
  if (upperValue.includes("INSIGHT_CANCELLED")) return "INSIGHT_CANCELLED";
  if (upperValue.includes("CANCEL")) return "INSIGHT_CANCELLED";
  if (upperValue.includes("COMPLETE") || upperValue.includes("FINISH")) {
    return "INSIGHT_COMPLETED";
  }
  if (upperValue.includes("LISTEN")) return "LISTENING";
  if (upperValue.includes("TALK") || upperValue.includes("SPEAK")) return "TALKING";
  if (upperValue.includes("THINK") || upperValue.includes("PROCESS")) return "THINKING";
  if (
    upperValue.includes("WAIT") ||
    upperValue.includes("PAUSE") ||
    upperValue.includes("IDLE")
  ) {
    return "WAITING";
  }
  if (upperValue.includes("READY")) return "DEFAULT";

  return upperValue;
};

export const isGuideStateTalking = (guideState) =>
  normalizeGuideStateValue(guideState?.guideState ?? guideState).includes("TALKING");

export const isGuideIdleForQuestions = (guideState) => {
  const normalizedState = normalizeGuideStateValue(
    guideState?.guideState ?? guideState,
  );
  if (!normalizedState) {
    return false;
  }

  return (
    normalizedState.includes("WAITING") ||
    normalizedState === "DEFAULT" ||
    normalizedState.includes("INSIGHT_COMPLETED") ||
    normalizedState.includes("INSIGHT_CANCELLED")
  );
};

export const normalizeGuideStatePayload = (payload) => {
  if (payload == null) {
    return null;
  }

  if (typeof payload === "string") {
    const trimmedPayload = payload.trim();
    if (!trimmedPayload) {
      return null;
    }

    try {
      return normalizeGuideStatePayload(JSON.parse(trimmedPayload));
    } catch {
      const normalizedState = normalizeGuideStateValue(trimmedPayload);
      const isKnownState = [
        "TALKING",
        "LISTENING",
        "THINKING",
        "WAITING",
        "DEFAULT",
        "INSIGHT_COMPLETED",
        "INSIGHT_CANCELLED",
      ].includes(normalizedState);

      return {
        guideState: isKnownState ? normalizedState : "UNKNOWN",
        rawGuideState: trimmedPayload,
        script: isKnownState ? "" : trimmedPayload,
        data: isKnownState ? "" : trimmedPayload,
        subtitleData: null,
        audioLength: 0,
        instructionText: "",
        hasInstructionText: false,
      };
    }
  }

  if (typeof payload !== "object") {
    return normalizeGuideStatePayload(String(payload));
  }

  const rawGuideState = payload.guideState ?? payload.state ?? payload.guide_state;
  const normalizedGuideState = normalizeGuideStateValue(rawGuideState);
  const hasScript = hasOwn(payload, "script");
  const hasInstructionText = hasOwn(payload, "instructionText");
  const script = normalizeGuideText(hasScript ? payload.script : payload.data);
  const audioLength = Number(payload.audioLength);

  return {
    ...payload,
    guideState: normalizedGuideState || "UNKNOWN",
    rawGuideState,
    script,
    data: script,
    subtitleData: payload.subtitleData ?? null,
    audioLength: Number.isFinite(audioLength) && audioLength > 0 ? audioLength : 0,
    instructionText: normalizeGuideText(payload.instructionText),
    hasInstructionText,
  };
};

export const getGuideDisplayUpdateFromPayload = (
  payload,
  previousInstructionText = "",
  { activeInsightId = "" } = {},
) => {
  const normalizedPayload = normalizeGuideStatePayload(payload);
  if (!normalizedPayload) {
    return {
      guideState: null,
      guideStateText: "",
      guideInstructionText:
        typeof previousInstructionText === "string" ? previousInstructionText : "",
      shouldClearAudio: true,
      shouldReadEmbeddedAudioLevel: false,
    };
  }

  const isInsightCompleted = normalizedPayload.guideState === "INSIGHT_COMPLETED";
  const isInsightCancelled = normalizedPayload.guideState === "INSIGHT_CANCELLED";
  const shouldClearScript = isInsightCompleted || isInsightCancelled;
  const nextScript = shouldClearScript ? "" : normalizedPayload.script;
  const guideState = {
    ...normalizedPayload,
    script: nextScript,
    data: nextScript,
    audioLength: shouldClearScript ? 0 : normalizedPayload.audioLength,
  };
  let guideInstructionText =
    typeof previousInstructionText === "string" ? previousInstructionText : "";
  const isTerminalInsightState = isInsightCompleted || isInsightCancelled;
  const payloadInsightId = getInsightIdFromPayload(normalizedPayload);
  const terminalInsightMatchesActiveInsight =
    isTerminalInsightState &&
    guideStepIdsMatch(payloadInsightId, activeInsightId);

  if (
    normalizedPayload.hasInstructionText &&
    (!isTerminalInsightState || terminalInsightMatchesActiveInsight)
  ) {
    guideInstructionText = normalizedPayload.instructionText;
  }

  return {
    guideState,
    guideStateText: nextScript,
    guideInstructionText,
    shouldClearAudio: shouldClearScript,
    shouldReadEmbeddedAudioLevel: !shouldClearScript,
  };
};

export const clampAudioLevel = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;

  const normalizedNumber = number > 1 && number <= 100 ? number / 100 : number;
  return Math.min(1, Math.max(0, normalizedNumber));
};

export const getGuideAudioLevelRawFromPayload = (
  payload,
  fallback = 0,
  { allowScalar = true } = {},
) => {
  if (payload == null) return fallback;

  if (typeof payload === "number") {
    return allowScalar ? payload : fallback;
  }

  if (typeof payload === "string") {
    const trimmedPayload = payload.trim();
    if (!trimmedPayload) return fallback;

    try {
      const parsedPayload = JSON.parse(trimmedPayload);
      return getGuideAudioLevelRawFromPayload(parsedPayload, fallback, {
        allowScalar,
      });
    } catch {
      if (!allowScalar) return fallback;

      const parsedNumber = Number(trimmedPayload.replace(/%$/, ""));
      return Number.isFinite(parsedNumber) ? parsedNumber : fallback;
    }
  }

  if (typeof payload === "object") {
    for (const field of GUIDE_AUDIO_LEVEL_FIELDS) {
      if (Object.prototype.hasOwnProperty.call(payload, field)) {
        return getGuideAudioLevelRawFromPayload(payload[field], fallback);
      }
    }
  }

  return fallback;
};
